/**
 * useImageDrop - Adds drag-and-drop capability to any existing image upload UI.
 *
 * Usage:
 *   const { dropProps, isDragging } = useImageDrop({
 *     onFile: (file) => handleImageUpload({ target: { files: [file] } }),
 *     accept: ['image/'], // optional MIME prefix filter (default: images)
 *     multiple: false,    // if true, calls onFile per file dropped
 *   });
 *
 *   <div {...dropProps} className={isDragging ? 'ring-2 ring-orange-500' : ''}>
 *     ...existing preview/button UI...
 *   </div>
 *
 * The hook synthesizes a standard file-input event so existing handlers can be
 * reused unchanged (they typically read event.target.files[0]).
 */
import { useCallback, useRef, useState } from 'react';

export default function useImageDrop({ onFile, accept = ['image/'], multiple = false } = {}) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const isAccepted = useCallback((file) => {
    if (!file) return false;
    if (!accept || accept.length === 0) return true;
    const type = file.type || '';
    return accept.some((prefix) => type.startsWith(prefix));
  }, [accept]);

  const onDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (e.dataTransfer?.items?.length) setIsDragging(true);
  }, []);

  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = Math.max(0, dragCounter.current - 1);
    if (dragCounter.current === 0) setIsDragging(false);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) return;
    const valid = files.filter(isAccepted);
    if (valid.length === 0) {
      alert('Please drop a valid image file.');
      return;
    }
    if (multiple) {
      valid.forEach((f) => onFile && onFile(f));
    } else {
      onFile && onFile(valid[0]);
    }
  }, [isAccepted, multiple, onFile]);

  return {
    dropProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
    isDragging,
  };
}
